import React from "react";

import {
  faCircleInfo,
  faPlus,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { FieldErrors } from "src/api/types";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { Switch } from "src/base-components/Switch";
import {
  CustomConnectionForm,
  getEmptyKeyValuePair,
} from "src/customConnectionNode/types";
import { Button } from "src/design-system/Button";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

const IGNORE_IF_EMPTY_TOOLTIP =
  "To control how empty values are handled, select which parameters to exclude from the request when their value is empty.";

type Props = {
  fieldName: "params" | "headers" | "body.value.key_values";
  addButtonText: string;
  immutable: boolean;
  runFieldErrors: FieldErrors | undefined;
  // Surfaces a per-row "Skip if empty" toggle. Only used for query params.
  showIgnoreIfEmpty?: boolean;
};

export const KeyValueArrayEditor: React.FC<Props> = ({
  fieldName,
  addButtonText,
  immutable,
  runFieldErrors,
  showIgnoreIfEmpty = false,
}) => {
  const { control } = useFormContext<CustomConnectionForm>();
  const { fields, append, remove } = useFieldArray({
    control: control,
    name: fieldName,
  });
  return (
    <div data-loc={`kv-editor-${fieldName}`}>
      {fields.length > 0 && (
        <div
          className={twJoin(
            "mb-1 flex font-inter-normal-13px text-gray-800",
            showIgnoreIfEmpty && "gap-x-2",
          )}
        >
          <span className="w-1/2">Key</span>
          <span className="w-1/2">Value</span>
          {showIgnoreIfEmpty && (
            <span className="flex w-24 shrink-0 items-center justify-center gap-x-1 whitespace-nowrap">
              Skip if empty
              <Tooltip
                body={IGNORE_IF_EMPTY_TOOLTIP}
                placement="top"
                title="Skip if empty"
                asChild
              >
                <span className="inline-flex">
                  <Icon icon={faCircleInfo} size="2xs" />
                </span>
              </Tooltip>
            </span>
          )}
          {/* To match the possible delete icon in other fex rows we add a ghost div */}
          {!immutable && <div className="w-4.5" />}
        </div>
      )}
      {fields.map((field, index) => (
        <div
          key={field.id}
          className={twJoin(
            "flex gap-x-2",
            index !== fields.length - 1 && "mb-1",
            index === fields.length - 1 && !immutable && "mb-1.5",
          )}
        >
          <Controller
            control={control}
            name={`${fieldName}.${index}.key`}
            render={({ field: controlledField }) => (
              <AutocompleteCodeInput
                dataLoc={`cc-node-${fieldName}.${index}.key`}
                disabled={immutable}
                error={runFieldErrors?.[field.id_key]}
                placeholder="Enter key"
                value={controlledField.value}
                plaintext
                onChange={controlledField.onChange}
              />
            )}
          />
          <Controller
            control={control}
            name={`${fieldName}.${index}.value`}
            render={({ field: controlledField }) => (
              <AutocompleteCodeInput
                dataLoc={`cc-node-${fieldName}.${index}.value`}
                disabled={immutable}
                error={runFieldErrors?.[field.id_value]}
                placeholder="data.field"
                value={controlledField.value}
                onChange={controlledField.onChange}
              />
            )}
          />
          {showIgnoreIfEmpty && (
            <Controller
              control={control}
              name={`${fieldName}.${index}.ignore_if_empty`}
              render={({ field: controlledField }) => (
                <div className="flex w-24 shrink-0 items-center justify-center">
                  <Tooltip
                    body={IGNORE_IF_EMPTY_TOOLTIP}
                    placement="top"
                    title="Skip if empty"
                    asChild
                  >
                    <span className="inline-flex">
                      <Switch
                        dataLoc={`cc-node-${fieldName}.${index}.ignore_if_empty`}
                        disabled={immutable}
                        enabled={Boolean(controlledField.value)}
                        onChange={(checked) =>
                          controlledField.onChange(checked)
                        }
                      />
                    </span>
                  </Tooltip>
                </div>
              )}
            />
          )}
          {!immutable && (
            <div className="flex items-center justify-center">
              <IconButton
                icon={faTrashCan}
                size="sm"
                onClick={() => remove(index)}
              />
            </div>
          )}
        </div>
      ))}
      {!immutable && (
        <Button
          isDisabled={immutable}
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={() => {
            append(
              showIgnoreIfEmpty
                ? { ...getEmptyKeyValuePair(), ignore_if_empty: false }
                : getEmptyKeyValuePair(),
            );
          }}
        >
          {addButtonText}
        </Button>
      )}
    </div>
  );
};
